import { useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { ResponseKind, WordleConfiguration } from "../../gql/graphql";
import { isWordleGame } from "../../graphql/helpers/isWordleGame";
import { RESPONSE_CREATE } from "../../graphql/mutations/Response";
import { useGlobalConfiguration } from "../../hooks/useGlobalConfiugration";
import { integrationApi } from "../../main";
import { GameHandlerProps } from "../../types";
import { NicknameModal } from "../shared/NicknameModal";
import { Background } from "../SwiperGame/Background";
import { Grid } from "./Grid/Grid";
import { Keyboard } from "./Keyboard/Keyboard";
import { ResultModal } from "./Result/ResultModal";
import { getStatuses } from "./statuses";

import styles from "./WordleGame.module.scss";

export const WordleGame = ({ game, finishGame }: GameHandlerProps) => {
  if (!isWordleGame(game)) {
    throw new Error("Invalid game type");
  }
  const { projectNickname } = useGlobalConfiguration();

  const { showLeaderboard, leaderboardConfiguration } = game.configuration as WordleConfiguration;

  const [nickname, setNickname] = useState<string | undefined>(projectNickname || undefined);
  const { isPreviewMode } = useGlobalConfiguration();
  const [guesses, setGuesses] = useState<string[]>([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [responseSlug, setResponseSlug] = useState<number | null>(null);
  const [responseCreate] = useMutation(RESPONSE_CREATE);
  const [isGameWon, setIsGameWon] = useState(false);
  const [isGameFinished, setIsGameFinished] = useState(false);

  const solution = game.configuration.word.toUpperCase();
  const tries = game.configuration.numberOfTries;
  const isLeaderboardVisible = showLeaderboard && !nickname;

  const statuses = getStatuses(solution, guesses);
  const score = isGameWon ? solution.length - guesses.length : 0;

  const onChar = (value: string) => {
    if (`${currentGuess}${value}`.length <= solution.length && guesses.length < tries) {
      setCurrentGuess(`${currentGuess}${value}`);
    }

    if (guesses.length === 0 && startTime === null && currentGuess.length === 0) {
      setStartTime(new Date());
    }
  };

  const onDelete = () => {
    setCurrentGuess(currentGuess.split("").slice(0, -1).join(""));
  };

  const saveResponse = async () => {
    if (!startTime) {
      return;
    }

    if (isPreviewMode) {
      return setResponseSlug(1);
    }

    const responseKind =
      integrationApi.configuration.provider === "alchemer" ? ResponseKind.Alchemer : ResponseKind.Hosted;

    const { data } = await responseCreate({
      variables: {
        data: {
          responseKind,
          startTime: startTime.toISOString(),
          endTime: new Date().toISOString(),
          nickname: nickname,
          gameId: game.id,
          wordleScores: [
            {
              score,
            },
          ],
        },
      },
    });

    if (data?.responseCreate?.slug) {
      setResponseSlug(data.responseCreate.slug);
    }
  };

  const endGame = () => {
    if (isPreviewMode) {
      return finishGame("");
    }

    finishGame(String(responseSlug) ?? "");
  };

  const onEnter = async () => {
    if (currentGuess.length < solution.length) {
      return;
    }

    if (currentGuess.length === solution.length && guesses.length < tries) {
      setGuesses([...guesses, currentGuess]);
      setCurrentGuess("");

      if (currentGuess === solution) {
        setIsGameWon(true);
        setIsGameFinished(true);
      }
    }

    if (guesses.length + 1 === tries) {
      setIsGameFinished(true);
    }
  };

  useEffect(() => {
    if (isGameFinished || isGameWon) {
      saveResponse();
    }
  }, [isGameFinished, isGameWon]);

  const showGame = !showLeaderboard || nickname;

  return (
    <Background gameVisuals={game.configuration.gameVisuals}>
      <NicknameModal
        isOpen={isLeaderboardVisible}
        gameId={game.id}
        themeColor={game.configuration.gameVisuals.themeColor}
        onContinue={setNickname}
        configuration={leaderboardConfiguration}
      />
      {showGame && (
        <div className={styles.container}>
          <Grid
            guesses={guesses}
            currentGuess={currentGuess}
            solution={solution}
            tries={tries}
            theme={game.configuration.gameVisuals}
          />
          <Keyboard
            onChar={onChar}
            onDelete={onDelete}
            onEnter={onEnter}
            statuses={statuses}
            theme={game.configuration.gameVisuals}
          />
          {responseSlug && (
            <ResultModal
              guesses={guesses}
              solution={solution}
              tries={tries}
              isVisible={true}
              score={score}
              onFinish={endGame}
              theme={game.configuration.gameVisuals}
            />
          )}
        </div>
      )}
    </Background>
  );
};
